import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import {
  listEmployeeTimes,
  type EmployeeTimeEntry,
} from "@/lib/employeeTimes";
import {
  getAuftragAppAngebot,
  type AppAngebotBundle,
  type AppAngebotPosition,
} from "@/lib/projectOverview";
import { formatHours } from "@/pages/projekte/hoursUsage";
import { hourBookingRequiresPreisartChoice } from "@/pages/admin/zeiterfassung/preisartChoice";

export interface AngebotPositionOption {
  id: number;
  label: string;
  position_nr: number | null;
  bezeichnung: string;
}

export function formatAngebotPositionLabel(pos: {
  position_nr?: number | null;
  bezeichnung?: string | null;
}): string {
  const nr = pos.position_nr != null ? `Pos. ${pos.position_nr}` : "Pos.";
  const name = String(pos.bezeichnung || "").trim() || "Ohne Bezeichnung";
  return `${nr} – ${name}`;
}

function preisartLabel(value: string | null | undefined): "Festpreis" | "Nachweis" | null {
  if (value === "nachweis") return "Nachweis";
  if (value === "festpreis") return "Festpreis";
  return null;
}

/** Geplantes Stundenkontingent einer Position (Lohnliste / Nachweis / Legacy). */
export function positionPlannedHours(position: AppAngebotPosition): number | null {
  const lohn = Array.isArray(position.lohn_liste) ? position.lohn_liste : [];
  if (lohn.length) {
    const sum = lohn.reduce((acc, zeile) => acc + (Number(zeile.stunden) || 0), 0);
    return sum > 0 ? sum : null;
  }
  if (
    position.preisart === "nachweis" &&
    position.nachweis_stunden != null &&
    Number(position.nachweis_stunden) > 0
  ) {
    return Number(position.nachweis_stunden);
  }
  if (position.stunden != null && Number(position.stunden) > 0) {
    return Number(position.stunden);
  }
  return null;
}

function isSelectablePosition(position: AppAngebotPosition): boolean {
  const typ = String(position.position_typ || "").trim().toLowerCase();
  return (typ === "artikel" || typ === "leistung") && position.id != null;
}

/** Positionen aus allen App-Angeboten des Projekts (dedupliziert). */
function collectSelectablePositions(bundle: AppAngebotBundle): AppAngebotPosition[] {
  const sources =
    Array.isArray(bundle.angebote) && bundle.angebote.length > 0
      ? bundle.angebote
      : [bundle];
  const seen = new Set<number>();
  const out: AppAngebotPosition[] = [];
  for (const offer of sources) {
    for (const pos of offer.positionen || []) {
      if (!isSelectablePosition(pos)) continue;
      const id = Number(pos.id);
      if (!Number.isFinite(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(pos);
    }
  }
  out.sort((a, b) => {
    const na = a.position_nr ?? a.sort_index ?? 0;
    const nb = b.position_nr ?? b.sort_index ?? 0;
    if (na !== nb) return na - nb;
    return Number(a.id) - Number(b.id);
  });
  return out;
}

function sumKalkStunden(bundle: AppAngebotBundle | null): number {
  if (!bundle) return 0;
  return (bundle.kalk_stunden || []).reduce((acc, item) => acc + (Number(item.stunden) || 0), 0);
}

function bookedHoursFromEntries(entries: EmployeeTimeEntry[]): {
  projectHours: number;
  byPosition: Map<number, number>;
} {
  const byPosition = new Map<number, number>();
  let projectHours = 0;
  for (const entry of entries) {
    if (entry.entry_type === "break") continue;
    const hours = Number(entry.hours_decimal) || 0;
    if (hours <= 0) continue;
    projectHours += hours;
    const posId = entry.geschaeftsdokument_position_id;
    if (posId != null && Number.isFinite(Number(posId))) {
      const id = Number(posId);
      byPosition.set(id, (byPosition.get(id) || 0) + hours);
    }
  }
  return { projectHours, byPosition };
}

interface AngebotPositionSelectProps {
  auftragId: number | null | undefined;
  value: number | null;
  onChange: (positionId: number | null) => void | Promise<void>;
  disabled?: boolean;
  label?: string;
  className?: string;
  selectSize?: "sm" | "md";
  /** Wird gesetzt, sobald das Projekt Nachweis- und Festpreispositionen hat. */
  onRequiredChange?: (required: boolean) => void;
  error?: string | null;
  /**
   * Auswahldialog von außen öffnen, ohne das Feld davor.
   * `onChange` darf ablehnen – der Dialog bleibt dann offen.
   */
  pickerOpen?: boolean;
  onPickerClose?: () => void;
  dialogTitle?: string;
  /** Kontext zur Buchung, steht über der Positionsliste. */
  dialogNote?: string | null;
}

/**
 * Optionale Auswahl einer App-Angebotsposition zum gewählten Projekt.
 * Öffnet ein Modal mit Langtext, Festpreis/Nachweis und Stundenkontingent.
 */
export function AngebotPositionSelect({
  auftragId,
  value,
  onChange,
  disabled,
  label = "Angebotsposition (optional)",
  className,
  selectSize = "md",
  onRequiredChange,
  error,
  pickerOpen,
  onPickerClose,
  dialogTitle,
  dialogNote,
}: AngebotPositionSelectProps) {
  const pickerControlled = pickerOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = pickerControlled ? Boolean(pickerOpen) : internalOpen;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bundle, setBundle] = useState<AppAngebotBundle | null>(null);
  const [bookedProjectHours, setBookedProjectHours] = useState(0);
  const [bookedByPosition, setBookedByPosition] = useState<Map<number, number>>(
    () => new Map(),
  );
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!auftragId) {
      setBundle(null);
      setBookedProjectHours(0);
      setBookedByPosition(new Map());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setBundle(null);
    void Promise.all([
      getAuftragAppAngebot(auftragId),
      listEmployeeTimes({ auftrag_id: auftragId }).catch(() => [] as EmployeeTimeEntry[]),
    ])
      .then(([nextBundle, entries]) => {
        if (cancelled) return;
        setBundle(nextBundle);
        const booked = bookedHoursFromEntries(Array.isArray(entries) ? entries : []);
        setBookedProjectHours(booked.projectHours);
        setBookedByPosition(booked.byPosition);
      })
      .catch(() => {
        if (!cancelled) {
          setBundle(null);
          setBookedProjectHours(0);
          setBookedByPosition(new Map());
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auftragId]);

  const positions = useMemo(
    () => (bundle ? collectSelectablePositions(bundle) : []),
    [bundle],
  );
  const choiceRequired = hourBookingRequiresPreisartChoice(positions);
  const onRequiredChangeRef = useRef(onRequiredChange);
  onRequiredChangeRef.current = onRequiredChange;

  useEffect(() => {
    onRequiredChangeRef.current?.(Boolean(auftragId) && choiceRequired);
  }, [auftragId, choiceRequired]);

  const selected = useMemo(
    () => (value != null ? positions.find((p) => Number(p.id) === value) ?? null : null),
    [positions, value],
  );

  const festpreisKontingent = sumKalkStunden(bundle);
  const nachweisKontingent =
    bundle?.nachweis_stunden != null && Number(bundle.nachweis_stunden) > 0
      ? Number(bundle.nachweis_stunden)
      : 0;
  const projectKontingent = festpreisKontingent + nachweisKontingent;

  const filteredPositions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return positions;
    return positions.filter((pos) => {
      const hay = [
        formatAngebotPositionLabel(pos),
        pos.bezeichnung,
        pos.beschreibung,
        preisartLabel(pos.preisart) || "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [positions, query]);

  if (!auftragId) {
    return null;
  }

  const effectiveLabel = choiceRequired ? "Nachweis oder Festpreis *" : label;
  const triggerLabel = loading
    ? "Positionen werden geladen…"
    : selected
      ? formatAngebotPositionLabel(selected)
      : positions.length === 0
        ? "Keine Angebotspositionen"
        : choiceRequired
          ? "Nachweis- oder Festpreisposition wählen"
          : "Position wählen (optional)";

  const selectedArt = selected ? preisartLabel(selected.preisart) : null;
  const selectedPlanned = selected ? positionPlannedHours(selected) : null;
  const selectedBooked =
    value != null ? bookedByPosition.get(value) || 0 : 0;

  function closePicker() {
    if (saving) return;
    setQuery("");
    if (pickerControlled) onPickerClose?.();
    else setInternalOpen(false);
  }

  function selectPosition(positionId: number | null) {
    if (!pickerControlled) {
      void onChange(positionId);
      setInternalOpen(false);
      setQuery("");
      return;
    }
    setSaving(true);
    void Promise.resolve()
      .then(() => onChange(positionId))
      .then(() => {
        setQuery("");
        onPickerClose?.();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  }

  const pickerTitle =
    dialogTitle && !choiceRequired
      ? dialogTitle
      : choiceRequired
        ? "Nachweis oder Festpreis"
        : dialogTitle || "Angebotsposition wählen";

  return (
    <>
      {pickerControlled ? null : (
        <div className={clsx("space-y-1", className)}>
          {effectiveLabel ? <span className="eh-label mb-0">{effectiveLabel}</span> : null}

          <button
            type="button"
            disabled={disabled || loading}
            onClick={() => setInternalOpen(true)}
            aria-invalid={error ? true : undefined}
            aria-required={choiceRequired || undefined}
            className={clsx(
              "eh-select-input relative w-full text-left",
              selectSize === "sm" && "text-[0.8125rem] !py-1.5 !pr-8 !pl-2.5",
              (!selected || loading) && "text-label-secondary",
              error && "border-status-orange",
            )}
            title={selected ? formatAngebotPositionLabel(selected) : undefined}
          >
            <span className="block truncate pr-1">{triggerLabel}</span>
            <span
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 h-1.5 w-1.5 -translate-y-[65%] rotate-45 border-r-2 border-b-2 border-label-tertiary"
            />
          </button>

          {error ? <p className="text-xs text-status-orange">{error}</p> : null}
          {choiceRequired && !error ? (
            <p className="text-[11px] leading-snug text-label-tertiary">
              Pflichtauswahl: Stunden auf Festpreispositionen erscheinen nicht auf dem Stundennachweis.
            </p>
          ) : null}

          {!loading && (projectKontingent > 0 || selected) ? (
            <p className="text-[11px] leading-snug text-label-tertiary">
              {selected ? (
                <>
                  {selectedArt ? `${selectedArt} · ` : null}
                  {selectedPlanned != null ? (
                    <>Kontingent {formatHours(selectedPlanned)}</>
                  ) : (
                    "Kein Stundenkontingent"
                  )}
                  {selectedBooked > 0 ? <> · erfasst {formatHours(selectedBooked)}</> : null}
                </>
              ) : (
                <>
                  Projekt
                  {festpreisKontingent > 0 ? <> · FP {formatHours(festpreisKontingent)}</> : null}
                  {nachweisKontingent > 0 ? <> · NW {formatHours(nachweisKontingent)}</> : null}
                  {bookedProjectHours > 0 ? (
                    <> · erfasst {formatHours(bookedProjectHours)}</>
                  ) : null}
                </>
              )}
            </p>
          ) : null}
        </div>
      )}

      <Dialog
        open={open}
        onClose={closePicker}
        title={pickerTitle}
        description={
          choiceRequired
            ? "Bitte auswählen, ob auf Nachweis- oder Festpreispositionen gearbeitet wurde."
            : "Langtext, Abrechnungsart und Stundenkontingent der Positionen."
        }
        size="lg"
        fullScreenMobile
        plainBackdrop={pickerControlled}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            {choiceRequired ? (
              <span />
            ) : (
              <Button
                type="button"
                variant="ghost"
                disabled={saving}
                onClick={() => selectPosition(null)}
              >
                Keine Position
              </Button>
            )}
            <Button type="button" variant="secondary" disabled={saving} onClick={closePicker}>
              Abbrechen
            </Button>
          </div>
        }
      >
        {dialogNote ? (
          <p className="mb-4 whitespace-pre-line text-sm text-label-secondary">{dialogNote}</p>
        ) : null}
        {saving ? (
          <p className="mb-3 text-sm text-label-tertiary">Position wird gespeichert …</p>
        ) : null}
        {loading || !bundle ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-label-secondary">
            <Spinner className="h-5 w-5" />
            Positionen werden geladen…
          </div>
        ) : (
          <div className={clsx("space-y-4", saving && "pointer-events-none opacity-60")}>
            <div className="rounded-ios border border-separator bg-fill-tertiary/30 px-3 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                Stundenkontingent Projekt
              </p>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-label">
                {festpreisKontingent > 0 ? (
                  <span>
                    Festpreis:{" "}
                    <strong className="tabular-nums font-semibold">
                      {formatHours(festpreisKontingent)}
                    </strong>
                  </span>
                ) : null}
                {nachweisKontingent > 0 ? (
                  <span>
                    Nachweis:{" "}
                    <strong className="tabular-nums font-semibold">
                      {formatHours(nachweisKontingent)}
                    </strong>
                  </span>
                ) : null}
                {projectKontingent <= 0 ? (
                  <span className="text-label-secondary">Kein Kontingent hinterlegt</span>
                ) : null}
                <span className="text-label-secondary">
                  Erfasst (App):{" "}
                  <span className="tabular-nums font-medium text-label">
                    {formatHours(bookedProjectHours)}
                  </span>
                </span>
              </div>
            </div>

            {positions.length > 5 ? (
              <TextInput
                label="Suchen"
                value={query}
                disabled={saving}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Bezeichnung, Langtext, Festpreis…"
                autoFocus
              />
            ) : null}

            {filteredPositions.length === 0 ? (
              <p className="py-6 text-center text-sm text-label-secondary">
                {positions.length === 0
                  ? "Keine wählbaren Angebotspositionen."
                  : "Keine Position passt zur Suche."}
              </p>
            ) : (
              <div className="space-y-4">
                {(choiceRequired
                  ? [
                      {
                        key: "nachweis",
                        title: "Nachweis",
                        items: filteredPositions.filter((pos) => pos.preisart === "nachweis"),
                      },
                      {
                        key: "festpreis",
                        title: "Festpreis",
                        items: filteredPositions.filter((pos) => pos.preisart !== "nachweis"),
                      },
                    ]
                  : [{ key: "alle", title: null as string | null, items: filteredPositions }]
                ).map((group) =>
                  group.items.length === 0 ? null : (
                    <div key={group.key} className="space-y-2">
                      {group.title ? (
                        <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                          {group.title}
                        </p>
                      ) : null}
                      <ul className="divide-y divide-separator overflow-hidden rounded-ios border border-separator">
                        {group.items.map((position) => {
                          const id = Number(position.id);
                          const art = preisartLabel(position.preisart);
                          const planned = positionPlannedHours(position);
                          const booked = bookedByPosition.get(id) || 0;
                          const isSelected = value != null && value === id;
                          const langtext = String(position.beschreibung || "").trim();

                          return (
                            <li key={id}>
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => selectPosition(id)}
                                className={clsx(
                                  "w-full px-3 py-3 text-left transition-colors hover:bg-fill-tertiary/40",
                                  isSelected && "bg-accent/10",
                                )}
                              >
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <p className="min-w-0 flex-1 text-sm font-semibold text-label">
                                    {formatAngebotPositionLabel(position)}
                                  </p>
                                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                                    {art ? (
                                      <span
                                        className={clsx(
                                          "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                                          art === "Nachweis"
                                            ? "bg-status-orange/15 text-status-orange"
                                            : "bg-status-blue/15 text-status-blue",
                                        )}
                                      >
                                        {art}
                                      </span>
                                    ) : null}
                                    {isSelected ? (
                                      <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent">
                                        gewählt
                                      </span>
                                    ) : null}
                                  </div>
                                </div>

                                {langtext ? (
                                  <p className="mt-1 whitespace-pre-wrap text-xs text-label-secondary line-clamp-4">
                                    {langtext}
                                  </p>
                                ) : (
                                  <p className="mt-1 text-xs italic text-label-tertiary">
                                    Kein Langtext
                                  </p>
                                )}

                                <p className="mt-1.5 text-xs text-label-secondary">
                                  {planned != null ? (
                                    <>
                                      Kontingent{" "}
                                      <span className="tabular-nums font-medium text-label">
                                        {formatHours(planned)}
                                      </span>
                                    </>
                                  ) : (
                                    "Kein Stundenkontingent"
                                  )}
                                  {" · "}
                                  erfasst{" "}
                                  <span className="tabular-nums font-medium text-label">
                                    {formatHours(booked)}
                                  </span>
                                </p>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
